import { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CrmLayout from "@/components/feature/CrmLayout";
import { statusBadgeColors, companyInfo as fallbackCompanyInfo } from "@/mocks/quotationsData";
import { computeSubtotal, computeTaxTotal, computeTotal } from "@/mocks/quotationsData";
import { SendQuotationModal } from "../components/SendQuotationModal";
import { useToast } from "@/components/base/Toast";
import type { QuotationLineItem } from "@/mocks/quotationsData";
import { fetchApi } from "@/services/apiService";

export default function QuotationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [quotation, setQuotation] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [companyDetails, setCompanyDetails] = useState(fallbackCompanyInfo);

  // 1. Fetch Dynamic Quotation
  const loadQuotation = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await fetchApi<any>(`/quotations/${id}`, undefined, null);
      if (res) {
        setQuotation(res);
      } else {
        setQuotation(null);
      }
    } catch (err: any) {
      console.error("Error loading quotation:", err);
      setQuotation(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadQuotation();
  }, [loadQuotation]);

  // 2. Fetch Company Details
  useEffect(() => {
    fetchApi<any>("/company", undefined, null)
      .then((res) => {
        const c = Array.isArray(res?.data) ? res.data[0] : res?.data || res;
        if (c?.name) {
          setCompanyDetails((prev) => ({
            ...prev,
            name: c.name || prev.name,
            address: c.address || prev.address,
            phone: c.phone || prev.phone,
            email: c.email || prev.email,
            gstin: c.gst_number || c.gstin || prev.gstin,
          }));
        }
      })
      .catch(() => {});
  }, []);

  const handleStatusChange = async (newStatus: string) => {
    if (!quotation) return;
    try {
      await fetchApi(`/quotations/${quotation.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      showToast(`Quotation marked as ${newStatus}`, newStatus === "Accepted" ? "success" : "info");
      loadQuotation();
    } catch (err: any) {
      showToast(err.message || "Failed to update status", "error");
    }
  };

  const handleDelete = async () => {
    if (!quotation) return;
    if (!window.confirm(`Are you sure you want to delete ${quotation.quoteNo || quotation.quote_number}?`)) return;
    try {
      await fetchApi(`/quotations/${quotation.id}`, { method: "DELETE" });
      showToast("Quotation deleted successfully", "success");
      navigate("/quotations");
    } catch (err: any) {
      showToast(err.message || "Failed to delete quotation", "error");
    }
  };

  const handleDownload = useCallback(() => {
    if (!quotation) return;
    showToast("Generating PDF...", "info");
    const container = document.createElement("div");
    container.className = "quotation-print-area";
    container.style.cssText = "display:none;";
    const items = quotation.items || [];
    const s = computeSubtotal(items);
    const tx = computeTaxTotal(items);
    const t = Number(quotation.total_amount) || computeTotal(items, quotation.discount || 0);
    container.innerHTML = generatePrintHtml(
      quotation.quoteNo || quotation.quote_number,
      quotation.leadName || quotation.client_name || "—",
      quotation.leadCompany || quotation.client_company || "",
      quotation.date || quotation.quote_date,
      quotation.validUntil || quotation.valid_until,
      items,
      quotation.discount || 0,
      quotation.terms || "",
      quotation.agent || "Unassigned",
      s,
      tx,
      t,
      companyDetails
    );
    document.body.appendChild(container);
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.body.removeChild(container);
      }, 500);
    }, 100);
  }, [quotation, showToast, companyDetails]);

  if (loading) {
    return (
      <CrmLayout title="Loading Quotation...">
        <div className="p-5 md:p-6 flex flex-col items-center justify-center min-h-[60vh]">
          <i className="ri-loader-4-line animate-spin text-3xl text-indigo-600 mb-3" />
          <p className="text-sm text-gray-500">Loading quotation details...</p>
        </div>
      </CrmLayout>
    );
  }

  if (!quotation) {
    return (
      <CrmLayout title="Quotation Not Found">
        <div className="p-5 md:p-6 flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
            <i className="ri-file-unknow-line text-2xl text-gray-400" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Quotation not found</h2>
          <p className="text-sm text-gray-500 mb-4">This quotation may have been deleted or the link is invalid.</p>
          <button
            onClick={() => navigate("/quotations")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line" /> Back to Quotations
          </button>
        </div>
      </CrmLayout>
    );
  }

  const items: QuotationLineItem[] = quotation.items || [];
  const subtotal = computeSubtotal(items);
  const taxTotal = computeTaxTotal(items);
  const total = Number(quotation.total_amount) || computeTotal(items, quotation.discount || 0);

  const formatDate = (d: string) => {
    if (!d || d === "—") return "—";
    const dt = new Date(d);
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const formatCurrency = (val: number) => "₹" + Number(val || 0).toLocaleString("en-IN");
  const totalFormatted = total >= 100000 ? "₹" + (total / 100000).toFixed(1) + "L" : "₹" + total.toLocaleString("en-IN");

  const quoteNum = quotation.quoteNo || quotation.quote_number || `QT-${quotation.id}`;
  const leadName = quotation.leadName || quotation.client_name || "—";
  const leadCompany = quotation.leadCompany || quotation.client_company || "";
  const leadPhone = quotation.leadPhone || quotation.client_phone || "";
  const leadEmail = quotation.leadEmail || quotation.client_email || "";
  const agentName = quotation.agent || "Unassigned";
  const quoteDate = quotation.date || quotation.quote_date;
  const validUntil = quotation.validUntil || quotation.valid_until;
  const status = quotation.status || "Draft";

  const statusTimeline = [
    { label: "Draft", date: quoteDate, done: true },
    {
      label: "Sent",
      date: status === "Sent" || status === "Accepted" || status === "Rejected" ? quoteDate : null,
      done: status !== "Draft",
    },
    {
      label: status === "Rejected" ? "Rejected" : "Accepted",
      date: status === "Accepted" || status === "Rejected" ? quoteDate : null,
      done: status === "Accepted" || status === "Rejected",
    },
  ];

  return (
    <CrmLayout title={`${quoteNum} — Quotation Detail`}>
      <div className="p-5 md:p-6 space-y-5">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <button onClick={() => navigate("/quotations")} className="hover:text-indigo-600 transition-colors cursor-pointer">
            Quotations
          </button>
          <i className="ri-arrow-right-s-line text-gray-300" />
          <span className="text-indigo-600 font-medium">{quoteNum}</span>
        </div>

        {/* Top action bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-bold text-gray-900">{quoteNum}</h1>
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${statusBadgeColors[status] || "bg-gray-100 text-gray-700"}`}>
              {status}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {status !== "Accepted" && (
              <button
                onClick={() => handleStatusChange("Accepted")}
                className="px-3 py-2 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-line mr-1" /> Mark Accepted
              </button>
            )}
            {status !== "Rejected" && (
              <button
                onClick={() => handleStatusChange("Rejected")}
                className="px-3 py-2 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line mr-1" /> Mark Rejected
              </button>
            )}
            <button
              onClick={() => navigate(`/quotations/${quotation.id}/edit`)}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line mr-1.5" />
              Edit
            </button>
            <button
              onClick={() => setSendModalOpen(true)}
              className="px-4 py-2 text-sm font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-send-plane-line mr-1.5" />
              Send
            </button>
            <button
              onClick={handleDownload}
              className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-download-line mr-1.5" />
              Download PDF
            </button>
            <button
              onClick={handleDelete}
              className="w-9 h-9 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors cursor-pointer"
              title="Delete Quotation"
            >
              <i className="ri-delete-bin-line text-sm" />
            </button>
          </div>
        </div>

        {/* Status timeline */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Status Timeline</h3>
          <div className="flex items-center gap-0">
            {statusTimeline.map((step, idx) => (
              <div key={step.label} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${step.done ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-400"}`}>
                    {step.done ? <i className="ri-check-line" /> : idx + 1}
                  </div>
                  <p className={`text-xs font-medium mt-1.5 whitespace-nowrap ${step.done ? "text-gray-900" : "text-gray-400"}`}>{step.label}</p>
                  {step.date && <p className="text-[10px] text-gray-400 mt-0.5">{formatDate(step.date)}</p>}
                </div>
                {idx < statusTimeline.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 mt-[-1.25rem] ${step.done && statusTimeline[idx + 1].done ? "bg-indigo-600" : "bg-gray-200"}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Quotation content */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-8">
            <div>
              <div className="w-12 h-12 rounded-lg bg-indigo-600 flex items-center justify-center mb-2">
                <span className="text-white text-sm font-bold">{companyDetails.name.slice(0, 2).toUpperCase()}</span>
              </div>
              <h2 className="text-lg font-bold text-gray-900">{companyDetails.name}</h2>
              <p className="text-sm text-gray-500">{companyDetails.address}</p>
              <p className="text-sm text-gray-500">{companyDetails.phone} | {companyDetails.email}</p>
              <p className="text-xs text-gray-400 mt-1">GSTIN: {companyDetails.gstin}</p>
            </div>
            <div className="text-right">
              <h1 className="text-2xl font-bold text-gray-900 tracking-wide">QUOTATION</h1>
              <p className="text-lg font-semibold text-indigo-600 mt-1">{quoteNum}</p>
              <div className="mt-3 space-y-0.5">
                <p className="text-sm text-gray-600">Date: <span className="font-medium text-gray-900">{formatDate(quoteDate)}</span></p>
                <p className="text-sm text-gray-600">Valid Until: <span className="font-medium text-gray-900">{formatDate(validUntil)}</span></p>
              </div>
            </div>
          </div>

          {/* Bill To */}
          <div className="border-t border-b border-gray-200 py-4 mb-6">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Bill To</p>
            <p className="text-base font-semibold text-gray-900">{leadName}</p>
            {leadCompany && <p className="text-sm text-gray-600">{leadCompany}</p>}
            {leadPhone && <p className="text-sm text-gray-600">{leadPhone}</p>}
            {leadEmail && <p className="text-sm text-gray-600">{leadEmail}</p>}
          </div>

          {/* Items Table */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 text-xs uppercase text-gray-500 tracking-wider">
                  <th className="text-left py-2.5 pr-3 font-semibold">#</th>
                  <th className="text-left py-2.5 pr-3 font-semibold">Description</th>
                  <th className="text-center py-2.5 px-3 font-semibold">Qty</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Unit</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Rate</th>
                  <th className="text-center py-2.5 px-3 font-semibold">Tax</th>
                  <th className="text-right py-2.5 pl-3 font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, idx) => {
                  const amt = (Number(item.qty) || 0) * (Number(item.rate) || 0) * (1 + (Number(item.taxPercent) || 0) / 100);
                  return (
                    <tr key={item.id || idx}>
                      <td className="py-2.5 pr-3 text-gray-400">{idx + 1}</td>
                      <td className="py-2.5 pr-3 font-medium text-gray-900">{item.description || "—"}</td>
                      <td className="py-2.5 px-3 text-center text-gray-700">{item.qty}</td>
                      <td className="py-2.5 px-3 text-gray-700">{item.unit || "Unit"}</td>
                      <td className="py-2.5 px-3 text-right text-gray-700">{formatCurrency(item.rate)}</td>
                      <td className="py-2.5 px-3 text-center text-gray-700">{item.taxPercent || 0}%</td>
                      <td className="py-2.5 pl-3 text-right font-medium text-gray-900">{formatCurrency(amt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-6">
            <div className="w-64 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-medium text-gray-900">{formatCurrency(subtotal)}</span>
              </div>
              {Number(quotation.discount) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Discount</span>
                  <span className="font-medium text-red-500">-{formatCurrency(Number(quotation.discount))}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Tax</span>
                <span className="font-medium text-gray-900">{formatCurrency(taxTotal)}</span>
              </div>
              <hr className="border-gray-200" />
              <div className="flex justify-between">
                <span className="text-base font-bold text-gray-900">Total</span>
                <span className="text-xl font-bold text-indigo-600">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          {/* Terms */}
          {quotation.terms && (
            <div className="border-t border-gray-200 pt-4 mb-6">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Terms &amp; Conditions</h3>
              <div className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">{quotation.terms}</div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-200 pt-4 flex justify-between items-end">
            <div>
              <p className="text-sm font-semibold text-gray-900">Prepared by</p>
              <p className="text-sm text-gray-600">{agentName}</p>
              <p className="text-xs text-gray-400 mt-1">This is a computer-generated quotation</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Authorized Signatory</p>
              <div className="mt-3 border-t border-gray-300 pt-1">
                <p className="text-xs text-gray-500">{companyDetails.name}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Send Modal */}
        {sendModalOpen && (
          <SendQuotationModal
            open={true}
            onClose={() => { setSendModalOpen(false); loadQuotation(); }}
            quoteId={quotation.id}
            quoteNo={quoteNum}
            leadName={leadName}
            leadEmail={leadEmail}
            totalFormatted={totalFormatted}
            validUntil={validUntil}
            agent={agentName}
            companyName={companyDetails.name}
            leadId={quotation.leadId || quotation.lead_id}
          />
        )}
      </div>
    </CrmLayout>
  );
}

function generatePrintHtml(
  quoteNo: string,
  leadName: string,
  leadCompany: string,
  date: string,
  validUntil: string,
  items: QuotationLineItem[],
  discount: number,
  terms: string,
  agent: string,
  subtotal: number,
  taxTotal: number,
  total: number,
  company: { name: string; address: string; phone: string; email: string; gstin: string }
) {
  const fmtDate = (d: string) => {
    if (!d || d === "—") return "—";
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };
  const fmtCurr = (v: number) => "₹" + Number(v || 0).toLocaleString("en-IN");
  const itemsHtml = items
    .map((item, idx) => {
      const amt = (Number(item.qty) || 0) * (Number(item.rate) || 0) * (1 + (Number(item.taxPercent) || 0) / 100);
      return `<tr><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;">${idx + 1}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;font-weight:500;">${item.description || "—"}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">${item.qty}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;">${fmtCurr(item.rate)}</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">${item.taxPercent || 0}%</td><td style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;font-weight:500;">${fmtCurr(amt)}</td></tr>`;
    })
    .join("");

  const discHtml =
    discount > 0
      ? `<div style="display:flex;justify-content:space-between;font-size:12px;padding:4px 0;"><span>Discount</span><span style="color:#ef4444;">-${fmtCurr(discount)}</span></div>`
      : "";

  return `<div style="font-family:Inter,system-ui,sans-serif;padding:20px 0;">
<div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;">
<div><div style="width:48px;height:48px;background:#4f46e5;border-radius:8px;display:flex;align-items:center;justify-content:center;margin-bottom:8px;"><span style="color:white;font-weight:bold;font-size:14px;">${company.name.slice(0, 2).toUpperCase()}</span></div><h2 style="font-size:18px;font-weight:bold;margin:0 0 4px;">${company.name}</h2><p style="font-size:12px;color:#6b7280;margin:0;">${company.address}</p><p style="font-size:12px;color:#6b7280;margin:0;">${company.phone} | ${company.email}</p><p style="font-size:11px;color:#9ca3af;margin:4px 0 0;">GSTIN: ${company.gstin}</p></div>
<div style="text-align:right;"><h1 style="font-size:24px;font-weight:bold;margin:0;letter-spacing:0.05em;">QUOTATION</h1><p style="font-size:18px;font-weight:600;color:#4f46e5;margin:4px 0 0;">${quoteNo}</p><p style="font-size:12px;margin:12px 0 0;">Date: ${fmtDate(date)}</p><p style="font-size:12px;margin:2px 0 0;">Valid Until: ${fmtDate(validUntil)}</p></div></div>
<hr style="border:0;border-top:2px solid #e5e7eb;margin:16px 0;" />
<div style="margin-bottom:16px;"><p style="font-size:11px;font-weight:600;color:#9ca3af;text-transform:uppercase;margin:0 0 4px;">Bill To</p><p style="font-size:16px;font-weight:600;margin:0;">${leadName || "—"}</p><p style="font-size:13px;color:#6b7280;margin:0;">${leadCompany || ""}</p></div>
<table style="width:100%;border-collapse:collapse;margin-bottom:16px;"><thead><tr style="background:#f3f4f6;"><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:left;">#</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:left;">Description</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">Qty</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;">Rate</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:center;">Tax</th><th style="padding:8px;border:1px solid #d1d5db;font-size:11px;text-align:right;">Amount</th></tr></thead><tbody>${itemsHtml}</tbody></table>
<div style="display:flex;justify-content:flex-end;margin-bottom:16px;"><div style="width:240px;"><div style="display:flex;justify-content:space-between;font-size:12px;padding:4px 0;"><span>Subtotal</span><span>${fmtCurr(subtotal)}</span></div>${discHtml}<div style="display:flex;justify-content:space-between;font-size:12px;padding:4px 0;"><span>Tax</span><span>${fmtCurr(taxTotal)}</span></div><hr style="border:0;border-top:1px solid #e5e7eb;margin:4px 0;" /><div style="display:flex;justify-content:space-between;font-size:15px;font-weight:bold;padding:4px 0;color:#4f46e5;"><span>Total</span><span>${fmtCurr(total)}</span></div></div></div>
${terms ? `<div style="border-top:2px solid #e5e7eb;padding-top:12px;margin-bottom:16px;"><p style="font-size:11px;font-weight:600;color:#9ca3af;text-transform:uppercase;margin:0 0 4px;">Terms &amp; Conditions</p><p style="font-size:11px;white-space:pre-line;margin:0;">${terms.replace(/\n/g, "<br>")}</p></div>` : ""}
<div style="border-top:2px solid #e5e7eb;padding-top:12px;display:flex;justify-content:space-between;"><div><p style="font-size:12px;font-weight:600;margin:0;">Prepared by</p><p style="font-size:12px;margin:2px 0 0;">${agent}</p></div><div style="text-align:right;"><p style="font-size:11px;margin:0;">Authorized Signatory</p></div></div>
<p style="text-align:center;font-size:11px;color:#9ca3af;margin-top:24px;">Thank you for your business &mdash; ${company.name}</p></div>`;
}